import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuthStore, useTestStore } from '../store';
import { localApi, initializeLocalApi } from '../utils/local-api';
import { api } from '../utils/api';
import { TestSession } from '../types';
import { ChevronLeft, ChevronRight, CheckCircle, Volume2, Play, Pause, AlertCircle, HelpCircle } from 'lucide-react';
import PinyinText from '../components/PinyinText';
import PartCountConfig from '../components/PartCountConfig';
import { extractImagePath, stripImagePathFromText } from '../utils/simulationQuestionPresentation';
import { getParts, getDefaults, parseLevel, snapToGroup } from '../config/hskTestParts';

const ListeningTest: React.FC = () => {
  // BACKEND_URL is now defined in vite.config.ts globally
  const API_BASE_URL = BACKEND_URL;

  const navigate = useNavigate();
  const { level: levelParam } = useParams<{ level: string }>();
  const HSK_LEVEL = parseLevel(levelParam);
  const TEST_PART = 'listening';
  const UNKNOWN_ANSWER = -1;
  const shouldShowPinyin = HSK_LEVEL === 2;
  const parts = getParts(HSK_LEVEL, 'listening');

  const { token } = useAuthStore();
  const { currentTest: storeCurrentTest, startTest, answerQuestion, nextQuestion, previousQuestion, completeTest, addTestToHistory, clearCurrentTest } = useTestStore();
  const displayLevel = storeCurrentTest?.testId?.startsWith('practice-') ? storeCurrentTest.level : HSK_LEVEL;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [completedSession, setCompletedSession] = useState<TestSession | null>(null);
  const [reviewIndex, setReviewIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioElement, setAudioElement] = useState<HTMLAudioElement | null>(null);
  const activeAudioRef = useRef<HTMLAudioElement | null>(null);
  const [localApiAvailable, setLocalApiAvailable] = useState(true);
  const [initializingApi, setInitializingApi] = useState(true);
  const [isCloudflare, setIsCloudflare] = useState(false);
  const [cloudSessionId, setCloudSessionId] = useState<string | null>(null);
  const [testNumber, setTestNumber] = useState(1);
  const [isCheckpointSaving, setIsCheckpointSaving] = useState(false);
  const [checkpointError, setCheckpointError] = useState('');
  const [showConfig, setShowConfig] = useState(false);
  const [listeningConfig, setListeningConfig] = useState<Record<string, number>>(() => getDefaults(HSK_LEVEL, 'listening'));

  const isSimulationMode = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('sim') === '1';
  const isReviewMode = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('review') === '1';
  const examScope = `hsk:2.0:simulation:${HSK_LEVEL}:${TEST_PART}:`;
  const currentTest = storeCurrentTest && (
    (isReviewMode && storeCurrentTest.testId?.startsWith('practice-')) || storeCurrentTest.examScope === examScope
  ) ? storeCurrentTest : null;

  // Reset config defaults when switching levels (the component instance is reused
  // across /test/:level/listening routes).
  useEffect(() => {
    setListeningConfig(getDefaults(HSK_LEVEL, 'listening'));
  }, [HSK_LEVEL]);

  const renderText = (text: string, className = '') => {
    const imagePath = extractImagePath(text);
    const displayText = imagePath ? stripImagePathFromText(text) : text;
    return <span className={className}>
      {shouldShowPinyin ? <PinyinText text={displayText} /> : displayText}
      {imagePath && <img src={imagePath.startsWith('http') ? imagePath : `${API_BASE_URL}/media/${imagePath.replace(/^\/+/, '')}`} alt="题目配图" className="mt-3 max-h-80 max-w-full rounded-lg border border-gray-200 bg-white object-contain" />}
    </span>;
  };

  useEffect(() => {
    if (token) {
        localApi.getHistory(token).then(history => {
            const listeningCount = (Array.isArray(history) ? history : []).filter((h: any) => h.part === 'listening').length;
            setTestNumber(listeningCount + 1);
        }).catch(e => {
            console.warn("Failed to fetch history for numbering", e);
        });
    }
  }, [token]);

  // Start (or resume) a listening test for the given per-part configuration.
  const loadAndStart = async (cfg: Record<string, number>) => {
    if (!token) return;

    const partCounts = parts
      .map(p => ({ part: p.key, count: snapToGroup(cfg[p.key] ?? 0, p.groupSize) }))
      .filter(item => item.count > 0);
    const totalQuestions = partCounts.reduce((sum, item) => sum + item.count, 0);
    if (totalQuestions <= 0) {
      setError('请至少设置 1 道听力题。');
      return;
    }

    try {
      setError('');
      setShowConfig(false);
      setInitializingApi(true);
      setLoading(true);

      const host = typeof window !== 'undefined' ? window.location.host : '';
      const cf = /workers\.dev|pages\.dev/i.test(host);
      setIsCloudflare(cf);
      const subparts = partCounts.map(item => item.part);

      if (cf) {
        setLocalApiAvailable(true);
        const res = await api.startTest(HSK_LEVEL, TEST_PART, totalQuestions, token, partCounts);
        setCloudSessionId(res.test_session_id);
        const questions = res.questions.map((q: any) => ({
          id: q.question_id,
          level: q.level ?? HSK_LEVEL,
          part: q.part || TEST_PART,
          question: q.question_text,
          options: q.options,
          correctAnswer: -1, // Hidden
          media: q.media,
          questionGroup: q.question_group ?? null
        }));

        let initialIndex = 0;
        let initialAnswers: (number | null)[] | undefined = undefined;

        if (res.resumed && res.progress) {
          console.log("Resuming Cloudflare test session:", res.test_session_id);
          initialIndex = res.progress.index || 0;
          if (res.progress.answers) {
              initialAnswers = new Array(questions.length).fill(null);
              Object.entries(res.progress.answers).forEach(([idxStr, ans]) => {
                  const idx = Number(idxStr);
                  if (idx >= 0 && idx < questions.length && typeof ans === 'number') {
                      // @ts-ignore
                      initialAnswers![idx] = ans;
                  }
              });
          }
        }

        startTest(HSK_LEVEL, TEST_PART, questions, res.test_session_id, initialIndex, initialAnswers, examScope);
        return;
      }

      const apiAvailable = await initializeLocalApi();
      setLocalApiAvailable(apiAvailable);
      if (!apiAvailable) {
        setError('Local API server is not running. Please start the local server first.');
        return;
      }

      const response = await localApi.startTest(HSK_LEVEL, TEST_PART, totalQuestions, token, subparts, partCounts);
      if (!response.success) {
        throw new Error('Failed to start test');
      }

      if (!response.questions || response.questions.length === 0) {
        throw new Error('题库暂无题目，请联系管理员添加听力题目后再试。');
      }

      const questions = response.questions.map(q => ({
        id: q.question_id,
        level: q.level ?? HSK_LEVEL,
        part: q.part || TEST_PART,
        question: q.question_text,
        options: q.options,
        correctAnswer: q.correct_index,
        media: q.media,
        questionGroup: (q as any).question_group ?? null
      }));

      let initialAnswers = undefined;
      let initialIndex = 0;

      if (response.resumed && response.progress) {
          console.log("Resuming test session:", response.test_id);
          initialIndex = response.progress.index || 0;
          if (response.progress.answers) {
              initialAnswers = new Array(questions.length).fill(null);
              Object.entries(response.progress.answers).forEach(([idxStr, ans]) => {
                  const idx = Number(idxStr);
                  if (idx >= 0 && idx < questions.length) {
                      // @ts-ignore
                      initialAnswers[idx] = ans;
                  }
              });
          }
      }

      startTest(HSK_LEVEL, TEST_PART, questions, response.test_id, initialIndex, initialAnswers, examScope);
    } catch (err: any) {
      setError(err.message || 'Failed to load Listening questions');
    } finally {
      setLoading(false);
      setInitializingApi(false);
    }
  };

  useEffect(() => {
    if (!token) {
      navigate('/');
      return;
    }

    const prepare = async () => {
      setInitializingApi(true);
      setLoading(true);

      // Review mode = "Review Wrong" navigated here with ?review=1, using
      // the in-memory practice session set by TestHistory. It must never
      // start/resume a server test and must never persist progress.
      const isReviewMode = new URLSearchParams(window.location.search).get('review') === '1';
      const currentIsPracticeSession = currentTest?.testId?.startsWith('practice-');

      if (isReviewMode && currentIsPracticeSession) {
        // Trust the practice session put in the store by startWrongPractice.
        setLoading(false);
        setInitializingApi(false);
        return;
      }

      // Coming back to a normal Listening Test while a stale practice session
      // is still in the store — clear it so we don't replay Review Wrong.
      if (!isReviewMode && currentIsPracticeSession) {
        clearCurrentTest();
      }

      // Check if current test matches expected part AND level (Isolation Check).
      // Practice sessions are excluded so they never trigger the resume path.
      const isMatchingTest = !currentIsPracticeSession &&
                             currentTest &&
                             currentTest.questions &&
                             currentTest.questions.length > 0 &&
                             currentTest.part === TEST_PART &&
                             currentTest.level === HSK_LEVEL;

      if (isMatchingTest) {
        // Verify the test is still in-progress on the server (catches cross-device completion)
        try {
          const activeRes = await localApi.getActiveTests(token);
          const isStillActive = (activeRes.active || []).some((t: any) => t.id === currentTest.testId);
          if (isStillActive) {
            setLoading(false);
            setInitializingApi(false);
            return;
          }
          // Test was completed on another device — fall through to start fresh
        } catch {
          // If check fails, trust localStorage (safe fallback)
          setLoading(false);
          setInitializingApi(false);
          return;
        }
      }

      // Full Simulation enters here with ?sim=1 — keep the fixed exam structure.
      if (isSimulationMode) {
        await loadAndStart(getDefaults(HSK_LEVEL, 'listening'));
        return;
      }

      // Standalone practice: let the student choose how many questions per part.
      setShowConfig(true);
      setLoading(false);
      setInitializingApi(false);
    };

    prepare();
  }, [token, startTest, navigate, HSK_LEVEL]);

  // Auto-save progress (skipped for client-only review/practice sessions)
  useEffect(() => {
    if (currentTest && currentTest.testId && token && !currentTest.testId.startsWith('practice-')) {
        const answersObj: Record<string, any> = {};
        currentTest.answers.forEach((ans, i) => {
            if (ans !== null) answersObj[i] = ans;
        });

        localApi.saveProgress(
            currentTest.testId,
            currentTest.currentQuestionIndex,
            answersObj,
            token
        ).catch(e => console.warn("Failed to auto-save", e));
    }
  }, [currentTest?.currentQuestionIndex, currentTest?.answers, currentTest?.testId, token]);

  // Handle browser close/refresh warning
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (currentTest && !showResults) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [currentTest, showResults]);

  const handleAnswer = (answerIndex: number) => {
    setSelectedAnswer(answerIndex);
    answerQuestion(answerIndex);
  };

  const handleNext = async () => {
    setSelectedAnswer(null);
    if (isCheckpointSaving) return;

    if (currentTest && currentTest.currentQuestionIndex < currentTest.questions.length - 1) {
      const isPracticeSession = currentTest.testId?.startsWith('practice-');
      if ((currentTest.currentQuestionIndex + 1) % 10 === 0 && currentTest.testId && token && !isPracticeSession) {
        setIsCheckpointSaving(true);
        setCheckpointError('');

        try {
          const idx = currentTest.currentQuestionIndex;
          const answersObj: Record<string, any> = {};

          currentTest.answers.forEach((ans, i) => {
              if (ans !== null) answersObj[i] = ans;
          });

          await localApi.saveProgress(currentTest.testId, idx, answersObj, token);
        } catch (err) {
          console.error("Checkpoint save failed:", err);
          setCheckpointError('网络连接异常，进度保存失败，请检查网络后重试。');
        } finally {
          setIsCheckpointSaving(false);
          nextQuestion();
        }
      } else {
        nextQuestion();
      }
    } else {
      handleComplete();
    }
  };

  const handlePrevious = () => {
    setSelectedAnswer(null);
    previousQuestion();
  };

  const handleComplete = async () => {
    if (!currentTest) return;

    const isPracticeSession = currentTest.testId?.startsWith('practice-');
    const session = completeTest();
    if (session) {
      setCompletedSession(session);
    }
    if (session && isPracticeSession) {
      addTestToHistory({
        ...session,
        score: 0,
      });
      setShowResults(true);
      return;
    }
    if (session && token && localApiAvailable) {
      try {
        const answers = session.answers.map((answer, index) => ({
          question_id: session.questions[index].id,
          chosen_option_index: answer === UNKNOWN_ANSWER ? null : answer,
          chose_buzhidao: answer === UNKNOWN_ANSWER,
          time_spent_seconds: 30
        }));
        if (isCloudflare && cloudSessionId) {
          await api.submitTest(cloudSessionId, answers, token);
          addTestToHistory(session);
        } else {
          const response = await localApi.submitTest(`hsk${HSK_LEVEL}_listening_session`, answers, token);
          if (response.success) {
            addTestToHistory(session);
          } else {
            throw new Error('Failed to submit test to local API');
          }
        }
      } catch (err: any) {
        console.error('Failed to submit Listening test:', err);
        setError(err.message || 'Failed to submit Listening test');

        try {
           const history = await localApi.getHistory(token);
           if (history.some((h: any) => h.id === session.id)) {
               setError('');
               addTestToHistory(session);
           }
        } catch (e) {
            // Ignore history fetch error
        }
      } finally {
        const params = new URLSearchParams(window.location.search);
        if (isCloudflare) {
          navigate('/history');
        } else {
          if (params.get('sim') === '1') {
            navigate(`/test/hsk${HSK_LEVEL}/reading?sim=1`);
          } else {
            setShowResults(true);
          }
        }
      }
    }
  };

  const [audioPlayedMap, setAudioPlayedMap] = useState<Record<string, boolean>>({});

  const playAudio = () => {
    if (audioElement && activeAudioRef.current === audioElement && !isPlaying && !audioPlayedMap[audioElement.src]) {
      const audioSrc = audioElement.src;
      audioElement.play().then(() => {
        if (activeAudioRef.current !== audioElement) return;
        setIsPlaying(true);
        setAudioPlayedMap(prev => ({ ...prev, [audioSrc]: true }));
      }).catch(err => {
        console.error("Audio playback failed:", err);
      });
    }
  };

  const [groupSelections, setGroupSelections] = useState<number[]>([]);

  const scrollToCard = (i: number) => {
    const el = document.getElementById(`q-${i}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    setReviewIndex(i);
  };

  useEffect(() => {
    if (currentTest && currentTest.questions && currentTest.answers) {
        const index = currentTest.currentQuestionIndex;
        if (currentTest.answers[index] !== undefined && currentTest.answers[index] !== null) {
            setSelectedAnswer(currentTest.answers[index]);
        } else {
            setSelectedAnswer(null);
        }

        // Review Wrong (practice-*) intentionally shows no "已选" hint —
        // the user explicitly asked for it to behave like a vanilla quiz.
        if (currentTest.testId?.startsWith('practice-')) {
            setGroupSelections([]);
            return;
        }

        // Determine peer indices that share an option pool with the current question.
        // Prefer real questionGroup metadata (works with any custom question count);
        // fall back to the fixed Q1-5 / Q6-10 ranges of the standard layout.
        const currentQ: any = currentTest.questions[index];
        const currentGroup = currentQ?.questionGroup ?? null;
        let peerIndices: number[] = [];

        if (currentGroup) {
            peerIndices = currentTest.questions
                .map((q: any, i) => ({ q, i }))
                .filter(({ q, i }) => i !== index && (q?.questionGroup ?? null) === currentGroup)
                .map(({ i }) => i);
        } else {
            let groupStart = -1;
            let groupEnd = -1;
            if (index >= 0 && index < 5) { groupStart = 0; groupEnd = 4; }
            else if (index >= 5 && index < 10) { groupStart = 5; groupEnd = 9; }
            if (groupStart !== -1) {
                for (let i = groupStart; i <= groupEnd; i++) {
                    if (i !== index) peerIndices.push(i);
                }
            }
        }

        const usedOptions: number[] = [];
        peerIndices.forEach((i) => {
            const ans = currentTest.answers[i];
            if (ans !== undefined && ans !== null && ans !== -1) {
                usedOptions.push(ans as number);
            }
        });
        setGroupSelections(usedOptions);
     }
  }, [currentTest?.currentQuestionIndex, currentTest?.answers]);


  const questionMedia = currentTest?.questions[currentTest.currentQuestionIndex]?.media;
  const questionAudioSrc = questionMedia
    ? questionMedia.startsWith('http')
      ? questionMedia
      : `${API_BASE_URL}/media/${questionMedia.startsWith('/') ? questionMedia.slice(1) : questionMedia}`
    : '';

  useEffect(() => {
    if (!questionAudioSrc) {
      setAudioElement(null);
      setIsPlaying(false);
      return;
    }

    const audio = new Audio(questionAudioSrc);
    activeAudioRef.current = audio;
    audio.onended = () => {
      if (activeAudioRef.current === audio) setIsPlaying(false);
    };
    setAudioElement(audio);
    setIsPlaying(false);

    const autoplayTimer = audioPlayedMap[questionAudioSrc] ? null : window.setTimeout(() => {
      if (activeAudioRef.current !== audio) return;
      audio.play().then(() => {
        if (activeAudioRef.current !== audio) return;
        setIsPlaying(true);
        setAudioPlayedMap(prev => ({ ...prev, [questionAudioSrc]: true }));
      }).catch(err => {
        if (activeAudioRef.current === audio) console.warn('Auto-play prevented by browser, waiting for user interaction:', err);
      });
    }, 500);

    return () => {
      if (autoplayTimer !== null) window.clearTimeout(autoplayTimer);
      if (activeAudioRef.current === audio) activeAudioRef.current = null;
      audio.onended = null;
      audio.pause();
      audio.removeAttribute('src');
    };
    // Recreate audio only when the question's media or test session changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionAudioSrc, currentTest?.testId]);

  if (loading || initializingApi) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-[#4F46E5] mx-auto mb-4"></div>
          <p className="text-[#312E81] font-bold">Initializing Test...</p>
        </div>
      </div>
    );
  }

  if (showResults && completedSession) {
    const total = completedSession.questions.length;
    const score = completedSession.answers.reduce((acc, answer, index) => {
      if (answer === completedSession.questions[index].correctAnswer) {
        return (acc || 0) + 1;
      }
      return acc || 0;
    }, 0);

    const isCorrect = (i: number) => {
      const a = completedSession.answers[i];
      return a !== null && a !== UNKNOWN_ANSWER && a === completedSession.questions[i].correctAnswer;
    };
    const isUnknown = (i: number) => completedSession.answers[i] === UNKNOWN_ANSWER;
    const isAnsweredWrong = (i: number) => {
      const a = completedSession.answers[i];
      return a !== null && a !== UNKNOWN_ANSWER && a !== completedSession.questions[i].correctAnswer;
    };
    const isReviewSession = completedSession.id?.startsWith('practice-');

    return (
      <div className="max-w-6xl mx-auto pb-12">
        {error && (
          <div className="mb-6 bg-orange-50 border border-orange-200 text-orange-800 px-4 py-3 rounded-2xl flex items-center shadow-sm">
            <AlertCircle className="w-5 h-5 mr-2" />
            <div>
              <p className="font-bold">Submission Warning</p>
              <p>{error}. Results shown below but may not be saved to history.</p>
            </div>
          </div>
        )}
        <div className="bg-white shadow-xl shadow-indigo-500/5 rounded-3xl p-8 border border-indigo-50">
          <div className="flex flex-col md:flex-row items-center justify-between mb-8 gap-4">
            <div>
              <h1 className="text-3xl font-bold text-[#312E81]">HSK {displayLevel}级 听力测试 回顾</h1>
              <p className="text-[#4F46E5] font-semibold mt-1">Score: {score}/{total} ({Math.round(((score || 0) / total) * 100)}%)</p>
            </div>
            <div className="flex gap-3">
              {!isReviewSession && (
                <button onClick={() => navigate('/')} className="px-6 py-2.5 bg-indigo-50 text-[#4F46E5] font-bold rounded-xl hover:bg-indigo-100 transition-colors">
                  Dashboard
                </button>
              )}
              <button onClick={() => navigate('/history')} className="px-6 py-2.5 bg-[#4F46E5] text-white font-bold rounded-xl hover:bg-indigo-700 transition-colors shadow-lg shadow-indigo-500/30">
                {isReviewSession ? 'Back to History' : 'History'}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
            <div className="md:col-span-3">
               <div className="sticky top-24 bg-gray-50 rounded-2xl p-4 max-h-[calc(100vh-8rem)] overflow-y-auto">
                  <h3 className="font-bold text-gray-700 mb-4">Question Map</h3>
                  <div className="grid grid-cols-5 gap-2">
                    {completedSession.questions.map((_, i) => {
                      const btnCorrect = isCorrect(i);
                      const btnUnknown = isUnknown(i);
                      const btnWrong = isAnsweredWrong(i);
                      const base = 'flex items-center justify-center h-10 rounded-lg text-sm font-bold transition-all';
                      const color = btnCorrect
                        ? 'bg-green-100 text-green-700 hover:bg-green-200'
                        : (btnUnknown || btnWrong)
                          ? 'bg-red-100 text-red-700 hover:bg-red-200'
                          : 'bg-gray-200 text-gray-600';
                      const active = i === reviewIndex ? 'ring-2 ring-offset-2 ring-[#4F46E5]' : '';
                      return (
                        <button key={i} onClick={() => scrollToCard(i)} className={`${base} ${color} ${active}`}>{i + 1}</button>
                      );
                    })}
                  </div>
               </div>
            </div>

            <div className="md:col-span-9 space-y-8">
              {completedSession.questions.map((qItem, idx) => {
                const ans = completedSession.answers[idx];
                const cardCorrect = ans !== null && ans !== UNKNOWN_ANSWER && ans === qItem.correctAnswer;
                const cardUnknown = ans === UNKNOWN_ANSWER;
                const cardClass = cardCorrect ? 'border-green-200 shadow-green-500/5' : 'border-red-200 shadow-red-500/5';
                return (
                  <div key={idx} id={`q-${idx}`} className={`p-6 md:p-8 bg-white border-2 rounded-3xl shadow-sm ${cardClass}`}>
                    <div className="flex items-center justify-between mb-6">
                      <div className="px-3 py-1 bg-gray-100 rounded-full text-sm font-bold text-gray-600">Question {idx + 1}</div>
                      <div className="text-sm font-bold">
                        {cardCorrect ? (
                          <span className="text-green-600 flex items-center gap-1"><CheckCircle className="w-4 h-4" /> Correct</span>
                        ) : cardUnknown ? (
                          <span className="text-orange-500 flex items-center gap-1"><HelpCircle className="w-4 h-4" /> Don't Know</span>
                        ) : (
                          <span className="text-red-500 flex items-center gap-1"><AlertCircle className="w-4 h-4" /> Incorrect</span>
                        )}
                      </div>
                    </div>
                    <div className="mb-6 text-xl font-medium text-[#312E81]">{renderText(qItem.question)}</div>
                    {qItem.media && (
                      <audio controls src={qItem.media.startsWith('http') ? qItem.media : `${API_BASE_URL}/media${qItem.media.startsWith('/') ? '' : '/'}${qItem.media}`} className="mb-6 w-full rounded-lg" />
                    )}
                    <div className="space-y-3">
                      {qItem.options.map((opt, oidx) => {
                        const isChosen = ans === oidx;
                        const isRight = qItem.correctAnswer === oidx;
                        const isImage = opt && /\.(webp|jpg|png|jpeg)$/i.test(opt);

                        let optStyle = "border-gray-100 bg-white";
                        if (isRight) optStyle = "border-green-500 bg-green-50 ring-1 ring-green-500";
                        else if (isChosen) optStyle = "border-red-500 bg-red-50 ring-1 ring-red-500";

                        return (
                          <div key={oidx} className={`p-4 border-2 rounded-xl transition-all ${optStyle}`}>
                            <div className="flex flex-col">
                              <div className="flex items-center gap-3">
                                <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${isRight ? 'bg-green-200 text-green-800' : isChosen ? 'bg-red-200 text-red-800' : 'bg-gray-100 text-gray-500'}`}>
                                    {String.fromCharCode(65 + oidx)}
                                </span>
                                {isImage ? (
                                  <img
                                    src={`${BACKEND_URL}/media/${opt}`}
                                    alt={`Option`}
                                    className="h-32 w-auto object-contain rounded-lg border border-gray-100 bg-white"
                                    onError={(e) => {
                                      (e.target as HTMLImageElement).src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyNCAyNCI+PHBhdGggZD0iTTEyIDJDMTYuNDEgMiAyMCA1LjU5IDIwIDEwVjE0QzIwIDE4LjQxIDE2LjQxIDIyIDEyIDIyQzcuNTkgMjIgNCAxOC40MSA0IDE0VjEwQzQgNS41OSA3LjU5IDIgMTIgMloiIGZpbGw9IiNjY2MiLz48L3N2Zz4=';
                                    }}
                                  />
                                ) : (
                                  renderText(opt, "text-gray-800 font-medium")
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto pt-20 text-center">
        <div className="bg-white p-8 rounded-3xl shadow-xl border border-red-100">
          <div className="w-16 h-16 bg-red-100 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Error Loading Test</h3>
          <p className="text-gray-500 mb-6">{error}</p>
          <button onClick={() => window.location.reload()} className="px-6 py-2 bg-gray-900 text-white rounded-xl font-bold">Try Again</button>
        </div>
      </div>
    );
  }

  if (showConfig && !currentTest) {
    return (
      <PartCountConfig
        title="自定义听力题量"
        subtitle="设置每个部分要练习的题目数量，然后开始听力测试。"
        Icon={Volume2}
        parts={parts}
        config={listeningConfig}
        onChange={setListeningConfig}
        onStart={() => loadAndStart(listeningConfig)}
        startLabel="开始听力测试"
        error={error}
      />
    );
  }

  if (!currentTest || !currentTest.questions || currentTest.questions.length === 0) {
    return null;
  }

  const currentQuestion = currentTest.questions[currentTest.currentQuestionIndex];
  if (!currentQuestion) return null;

  const progress = ((currentTest.currentQuestionIndex + 1) / currentTest.questions.length) * 100;

  return (
    <div className="max-w-4xl mx-auto pb-20">
      {/* Progress Header */}
      <div className="mb-8">
         <div className="flex justify-between items-end mb-2 px-1">
             <div>
                 <span className="text-sm font-bold text-[#4F46E5] uppercase tracking-wider">HSK {displayLevel} Listening</span>
                 <h2 className="text-2xl font-bold text-[#312E81]">Question {currentTest.currentQuestionIndex + 1} <span className="text-gray-400 text-lg">/ {currentTest.questions.length}</span></h2>
             </div>
             <div className="text-right">
                 <div className="text-sm font-bold text-gray-500">Test {testNumber}</div>
             </div>
         </div>
         <div className="h-4 bg-white rounded-full p-1 shadow-sm">
            <div className="h-full bg-[#22C55E] rounded-full transition-all duration-500 relative overflow-hidden" style={{ width: `${progress}%` }}>
                <div className="absolute inset-0 bg-white/20 animate-[shimmer_2s_infinite]"></div>
            </div>
         </div>
      </div>

      <div className="bg-white rounded-[2rem] p-6 md:p-8 shadow-xl shadow-indigo-900/5 border border-indigo-50 relative overflow-hidden">
         {/* Decorative Circle */}
         <div className="absolute -top-20 -right-20 w-64 h-64 bg-[#EEF2FF] rounded-full opacity-50 pointer-events-none"></div>

         {/* Audio Section */}
         {currentQuestion.media && (
            <div className="relative z-10 mb-8">
               <div className="bg-[#4F46E5] rounded-2xl p-4 md:p-6 flex items-center gap-4 text-white shadow-lg shadow-indigo-500/30 transition-transform hover:scale-[1.01]">
                   <button
                      onClick={playAudio}
                      disabled={audioElement ? audioPlayedMap[audioElement.src] : false}
                      className={`w-14 h-14 rounded-full flex items-center justify-center shrink-0 transition-all ${
                          isPlaying
                            ? 'bg-white text-[#4F46E5]'
                            : (audioElement && audioPlayedMap[audioElement.src])
                               ? 'bg-indigo-400 text-indigo-200 cursor-not-allowed'
                               : 'bg-white/20 hover:bg-white/30 text-white'
                      }`}
                   >
                       {isPlaying ? <Pause className="w-6 h-6 fill-current" /> : (audioElement && audioPlayedMap[audioElement.src]) ? <CheckCircle className="w-6 h-6" /> : <Play className="w-6 h-6 fill-current ml-1" />}
                   </button>
                   <div className="flex-1">
                       <div className="font-bold text-lg mb-1">
                           {(audioElement && audioPlayedMap[audioElement.src]) ? "Audio Played" : isPlaying ? "Playing..." : "Play Audio"}
                       </div>
                       <div className="text-indigo-200 text-sm">
                           {(audioElement && audioPlayedMap[audioElement.src]) ? "You can only listen once." : "Listen carefully."}
                       </div>
                   </div>
                   <Volume2 className="w-8 h-8 text-indigo-300 opacity-50" />
               </div>
            </div>
         )}

         {/* Question Text */}
         <div className="relative z-10 mb-8">
            <h3 className="text-xl md:text-2xl font-bold text-[#312E81] leading-relaxed">
                {renderText(currentQuestion.question)}
            </h3>
         </div>

         {/* Options Grid */}
         <div className="relative z-10">
            {(() => {
              const isImageOption = (opt: string) => opt && /\.(webp|jpg|png|jpeg)$/i.test(opt);
              const hasImageOptions = currentQuestion.options && Array.isArray(currentQuestion.options) && currentQuestion.options.some(isImageOption);

              const isFirstTenQuestions = currentTest.currentQuestionIndex < 10;
              const gridClass = isFirstTenQuestions
                ? "grid grid-cols-1 md:grid-cols-3 gap-4"
                : hasImageOptions
                  ? "grid grid-cols-1 md:grid-cols-2 gap-4"
                  : "space-y-3";

              return (
                <div className={gridClass}>
                  {currentQuestion.options.map((option, index) => {
                     if (!option) return null;
                     const isUsedInGroup = groupSelections.includes(index);

                     return (
                    <button
                      key={index}
                      onClick={() => handleAnswer(index)}
                      className={`
                        text-left group cursor-pointer rounded-2xl p-4 border-2 transition-all duration-200 relative
                        ${selectedAnswer === index
                            ? 'border-[#4F46E5] bg-indigo-50 shadow-md scale-[1.02] z-10'
                            : isUsedInGroup
                                ? 'border-gray-100 bg-gray-50 opacity-60'
                                : 'border-gray-100 hover:border-indigo-200 hover:bg-gray-50'
                        }
                        ${hasImageOptions ? 'flex flex-col items-center justify-center' : 'flex items-center gap-4'}
                      `}
                    >
                      {/* Selection Indicator */}
                      {selectedAnswer === index && (
                          <div className="absolute top-3 right-3 text-[#4F46E5]">
                              <CheckCircle className="w-6 h-6 fill-current" />
                          </div>
                      )}

                      {isUsedInGroup && (
                          <div className="absolute top-2 right-2 bg-gray-200 text-gray-500 text-xs px-2 py-1 rounded-full font-bold">
                              Selected
                          </div>
                      )}

                      <div className={`
                        w-10 h-10 rounded-xl flex items-center justify-center font-bold text-lg transition-colors shrink-0
                        ${selectedAnswer === index
                            ? 'bg-[#4F46E5] text-white'
                            : 'bg-gray-100 text-gray-400 group-hover:bg-indigo-100 group-hover:text-[#4F46E5]'
                        }
                        ${hasImageOptions ? 'mb-3' : ''}
                      `}>
                        {String.fromCharCode(65 + index)}
                      </div>

                      {isImageOption(option) ? (
                        <img
                          src={`${BACKEND_URL}/media/${option}`}
                          alt={`Option`}
                          className={`h-32 w-full object-contain rounded-lg ${isUsedInGroup ? 'grayscale' : ''}`}
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyNCAyNCI+PHBhdGggZD0iTTEyIDJDMTYuNDEgMiAyMCA1LjU5IDIwIDEwVjE0QzIwIDE4LjQxIDE2LjQxIDIyIDEyIDIyQzcuNTkgMjIgNCAxOC40MSA0IDE0VjEwQzQgNS41OSA3LjU5IDIgMTIgMloiIGZpbGw9IiNjY2MiLz48L3N2Zz4=';
                          }}
                        />
                      ) : (
                        renderText(option, `font-medium text-lg ${isUsedInGroup ? 'line-through text-gray-400' : 'text-gray-700'}`)
                      )}
                    </button>
                  )})}

                  {/* Don't Know Button */}
                  <button
                    onClick={() => handleAnswer(UNKNOWN_ANSWER)}
                    className={`
                        text-left group cursor-pointer rounded-2xl p-4 border-2 transition-all duration-200
                        ${selectedAnswer === UNKNOWN_ANSWER
                            ? 'border-orange-400 bg-orange-50 shadow-md scale-[1.02] z-10'
                            : 'border-gray-100 hover:border-orange-200 hover:bg-orange-50/50'
                        }
                        ${hasImageOptions ? 'flex flex-col items-center justify-center' : 'flex items-center gap-4'}
                    `}
                  >
                    <div className={`
                        w-10 h-10 rounded-xl flex items-center justify-center font-bold text-lg transition-colors shrink-0
                        ${selectedAnswer === UNKNOWN_ANSWER
                            ? 'bg-orange-500 text-white'
                            : 'bg-orange-100 text-orange-400 group-hover:bg-orange-200 group-hover:text-orange-600'
                        }
                        ${hasImageOptions ? 'mb-3' : ''}
                      `}>
                        ?
                    </div>
                    <span className={`font-medium text-lg ${selectedAnswer === UNKNOWN_ANSWER ? 'text-orange-700' : 'text-gray-500'}`}>
                        不知道 (Don't know)
                    </span>
                  </button>
                </div>
              );
            })()}
         </div>

         {/* Navigation Buttons */}
         {checkpointError && (
             <div className="mt-6 p-4 bg-red-50 text-red-600 rounded-lg border border-red-200">
                 {checkpointError}
             </div>
         )}
         <div className="mt-8 pt-8 border-t border-gray-100 flex justify-between items-center">
            <button
              onClick={handlePrevious}
              disabled={currentTest.currentQuestionIndex === 0 || isCheckpointSaving}
              className="px-6 py-3 rounded-xl font-bold text-gray-400 hover:bg-gray-50 hover:text-gray-600 disabled:opacity-30 disabled:hover:bg-transparent transition-colors flex items-center gap-2"
            >
              <ChevronLeft className="w-5 h-5" />
              Previous
            </button>

            <button
              onClick={handleNext}
              disabled={isCheckpointSaving}
              className="px-8 py-3 bg-[#22C55E] hover:bg-[#16A34A] text-white font-bold text-lg rounded-2xl shadow-lg shadow-green-500/30 transition-all hover:-translate-y-0.5 active:translate-y-0 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isCheckpointSaving ? 'Saving...' : currentTest.currentQuestionIndex === currentTest.questions.length - 1 ? 'Complete Test' : 'Next Question'}
              <ChevronRight className="w-5 h-5" />
            </button>
         </div>

      </div>
    </div>
  );
};

export default ListeningTest;
